import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Message } from "@ag-ui/client";
import { ChevronDown } from "lucide-react";
import useToast from "~/components/ui/toast";
import type {
  AgentConnector,
  AgentStarterPrompt,
  ConversationAgentSummary,
  PartialAgent,
} from "~/services/agents";
import { uploadFilesToConversation } from "~/services/artifacts";
import { FeaturedSession } from "~/services/projects";
import type { PartialSkill } from "~/services/skills";
import { ConversationTurns } from "./ConversationTurns";
import { EmptyConversationView } from "./EmptyConversationView";
import { McpAppEntriesProvider } from "./McpAppEntriesContext";
import type { ReplayHttpAgent } from "./ReplayHttpAgent";
import { ToolUIProvider } from "./ToolUIContext";
import { NEXTGEN_CHAT_EVENT, trackNextgenChatEvent } from "./analytics";
import { answerErrorMessage } from "./answerError";
import {
  type AnswerChannel,
  awaitingChannels,
  type PendingInput,
  resumeEntries,
} from "./awaitingInput";
import { Composer } from "./composer/Composer";
import type { McpGuidelines } from "./connectors/useMcpGuidelines";
import { parseContextBuffer } from "./contextBuffer";
import { latestTurnAgentId } from "./nextTurnAgent";
import { TOOL_UI_REGISTRY } from "./tool-ui-registry";
import { useAgent } from "./useAgent";
import { BOTTOM_THRESHOLD, useAnchoredScroll } from "./useAnchoredScroll";
import { useConversationAnalytics } from "./useConversationAnalytics";
import { useDatasetDescriptionSync } from "./useDatasetDescriptionSync";
import { useFirstMessageDelivery } from "./useFirstMessageDelivery";
import { useInitializationPanelSync } from "./useInitializationPanelSync";
import { useMcpAppEvents } from "./useMcpAppEvents";
import { useMcpAppsPaneSync } from "./useMcpAppsPane";
import { usePendingComposerDraft } from "./usePendingComposerDraft";
import { usePlanPanelSync } from "./usePlanPanelSync";
import { useRunError } from "./useRunError";
import { useRunReconcile } from "./useRunReconcile";
import { useSessionDagRunsSync } from "./useSessionDagRuns";
import { useSessionEvalRunsSync } from "./useSessionEvalRuns";
import { useSessionIngestsSync } from "./useSessionIngests";
import { useSessionPlanSync } from "./useSessionPlan";
import { useSkillSync } from "./useSkillSync";
import { useStreamingTurnIds } from "./useStreamingTurnIds";
import { SubagentEventsProvider, useSubagentEvents } from "./useSubagentEvents";
import { useTurns } from "./useTurns";

const NO_AWAITING_CALLS: ReadonlyMap<string, AnswerChannel> = new Map();

// An accepted handoff's re-posed message, held until the switch to `agentId`
// lands and that agent's connectors settle.
interface PendingRepose {
  agentId: string;
  text: string;
  attachmentIds: string[];
}

interface ChatProps {
  agent: ReplayHttpAgent;
  conversationId: string;
  // The effective next-turn agent's full roster record, for the empty-view
  // guide card (its identity, description, and guide content). Null while it
  // is still resolving, or when the project has none.
  emptyViewAgent: PartialAgent | null;
  // True when /connect reported MESSAGE_IN_PROGRESS — attach to the server's
  // in-flight SSE stream on mount instead of starting a new run.
  resumeInFlight: boolean;
  // What /connect reported the conversation waiting on at mount.
  initialPending: PendingInput;
  // Skills available to the composer's slash-command picker; fetched at the
  // conversation level so a new conversation refreshes the list.
  skills: PartialSkill[];
  // Maps a DATASET skill's scope_id → its dataset display name, for the
  // "registered from" info tooltip in the skills picker.
  datasetNames: Record<string, string>;
  // Connectors granted to this conversation's agent and the names currently
  // enabled. Selection is owned by the conversation level and persisted there.
  connectors: AgentConnector[];
  enabledConnectorNames: string[];
  onToggleConnector: (connectorName: string) => void;
  // MCP-guidelines acceptance gate for the connectors picker.
  mcpGuidelines: McpGuidelines;
  // Per-turn connector allow-list sent as `forwardedProps.connectors`, or
  // `undefined` while loading so the backend can pick its default behavior.
  forwardedConnectors: string[] | undefined;
  starterPrompts: AgentStarterPrompt[];
  // Names of the dataset(s) backing this conversation's context, surfaced as a
  // folder indicator in the composer toolbar. A single entry today.
  contextDatasetNames?: string[];
  // True while connector grants / MCP-guidelines acceptance load; the queued
  // first turn waits so it forwards the splash's connector selection, not the
  // backend default, and an accepted handoff's re-posed turn waits so it
  // forwards the new agent's selection.
  connectorsLoading?: boolean;
  // Project this conversation belongs to, and whether the caller owns it —
  // gates the composer share popover's "Add to Featured Sessions" toggle.
  projectId?: string;
  isOwner: boolean;
  isFeatured: boolean;
  // Needed to feature the session from the composer's share popover: the rail
  // stores a name and agent snapshot, not a reference back to the conversation.
  displayName: string;
  agentId?: string | null;
  // True once the roster and the session list have loaded and the project still
  // has no usable agent to resolve `agentId` to. Computed by the parent so the
  // explanation under the composer doesn't flash on while those are loading.
  projectNeedsAgent: boolean;
  // Advances the lifted next-turn agent id when the composer switcher fires (or a
  // handoff suggestion is accepted), so the parent re-keys skills/connectors on
  // the new agent and persists the selection.
  onAgentSwitched: (agentId: string) => void;
  featuredSessions: FeaturedSession[] | undefined;
  // Resolves a turn's handler id to its identity, so each turn is labeled with
  // the agent that handled it rather than the conversation's current agent. Keyed
  // by agent id; the project's usable agents plus the conversation's current agent.
  agentsById: ReadonlyMap<string, ConversationAgentSummary>;
  // The project's usable (attached + ENABLED) agent ids — narrower than
  // `agentsById`, which also carries the conversation's current agent for
  // display even once it is no longer usable. Gates the latest turn's handoff
  // suggestion so it never offers an agent outside this set.
  usableAgentIds: ReadonlySet<string>;
  // Whether this conversation has a durable plan, from the `/connect` snapshot.
  // Gates the durable plan-checklist fetch (see `conversationPlanKey`) on the
  // conversation's own data rather than the current agent's planning capability.
  hasPlan: boolean;
  // Dismisses the latest turn's handoff suggestion (keyed on its turn id). The
  // parent records the dismissal in sessionStorage so it survives a reload;
  // `dismissedSuggestionTurnId` is that stored id, hiding the matching suggestion.
  onDismissSuggestion?: (turnId: string) => void;
  dismissedSuggestionTurnId?: string | null;
}

export const Chat = ({
  agent: rawAgent,
  conversationId,
  emptyViewAgent,
  starterPrompts,
  resumeInFlight,
  initialPending,
  skills,
  datasetNames,
  connectors,
  enabledConnectorNames,
  onToggleConnector,
  mcpGuidelines,
  forwardedConnectors,
  contextDatasetNames,
  connectorsLoading,
  projectId,
  isOwner,
  isFeatured,
  displayName,
  agentId,
  projectNeedsAgent,
  onAgentSwitched,
  featuredSessions,
  agentsById,
  usableAgentIds,
  hasPlan,
  onDismissSuggestion,
  dismissedSuggestionTurnId,
}: ChatProps) => {
  const { agent: agentBase } = useAgent(rawAgent);
  const agent = agentBase as ReplayHttpAgent;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showError } = useToast();
  // Agent id + display name ride on every chat analytics event; both track the
  // live switched agent, resolved from the roster — never a borrowed identity,
  // so an agent outside the roster names no agent at all rather than pairing
  // its id with another agent's name.
  const agentName = (agentId ? agentsById.get(agentId)?.display_name : undefined) ?? null;

  const [showJump, setShowJump] = useState(false);
  // `agentId` is the next-turn agent, lifted to the parent so skills/connectors
  // re-key on a switch; it drives the composer switcher. The live turn's handler
  // is stamped on the user message at submit (see `startTurn`), so the streaming
  // hook only tracks the run id and timestamp.
  const liveTurnMeta = useStreamingTurnIds(agent);
  const runError = useRunError(agent);
  const subagentEvents = useSubagentEvents(agent);
  const mcpAppEntries = useMcpAppEvents(rawAgent);
  useMcpAppsPaneSync(rawAgent, agent.messages);
  useSessionIngestsSync(agent.messages);
  useSessionEvalRunsSync(agent.messages);
  useSessionDagRunsSync(agent.messages);
  useSessionPlanSync(agent.messages, agent.isRunning, conversationId, hasPlan);
  useSkillSync(rawAgent);
  useInitializationPanelSync(rawAgent);
  useDatasetDescriptionSync(rawAgent);
  usePlanPanelSync(rawAgent);
  // Fires analysis_agent_message_received / _error off the run lifecycle; the
  // sent / cancelled counterparts are fired from their handlers below.
  useConversationAnalytics(agent, { conversationId, projectId, agentId, agentName });
  const turns = useTurns(agent.messages, agent.isRunning, liveTurnMeta, runError);
  const { isCancelling, reconciling, pending, cancel, supersedeCancel } = useRunReconcile(
    agent,
    conversationId,
    turns,
    resumeInFlight,
    initialPending
  );
  // useAgent re-renders on state changes, so this stays current.
  const contextBuffer = parseContextBuffer(agent.state);

  // Owns the scroll position (bottom pin, follow, deep link). `turns` is the
  // follow key — its identity changes as messages stream in. `releaseHold` ends
  // an active deep-link hold when the user takes over below.
  const { highlightedTurnId, releaseHold } = useAnchoredScroll(scrollRef, turns);

  const pausedCalls = useMemo(
    () => awaitingChannels(pending, agent.messages),
    [pending, agent.messages]
  );
  useEffect(() => {
    agent.pendingToolCallIds = new Set(pending.pending_tool_call_ids);
  }, [agent, pending]);
  const settled = !agent.isRunning && !reconciling && !isCancelling;

  // A HITL pause blocks the composer until the user answers via its card —
  // free-form chat can't satisfy the deferred result the backend awaits. While
  // a run is in flight or being reconciled the local view isn't trustworthy
  // yet, so nothing is surfaced as awaiting until it settles.
  const awaitingToolCalls = settled ? pausedCalls : NO_AWAITING_CALLS;
  const awaitingAnswer = awaitingToolCalls.size > 0;

  const recomputeJump = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > BOTTOM_THRESHOLD);
  }, []);

  // Keep the jump-to-bottom button in sync as content grows; the scroll itself
  // is owned by `useAnchoredScroll`.
  useEffect(() => {
    recomputeJump();
  }, [turns, recomputeJump]);

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    releaseHold();
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // Build the per-turn `forwardedProps` payload.
  // `agent_id` names the agent the user selected to handle this turn — the
  // analysis-agent runs it as that agent (the UI is the source of truth), so a
  // switch takes effect without a separate request. Every new turn carries one.
  // `attachment_artifact_ids` is the signal the analysis-agent reads to announce
  // the files to the model; connectors ride the same channel.
  const runOptions = useCallback(
    (turnAgentId: string, attachmentIds: string[]) => {
      const forwardedProps: Record<string, unknown> = { agent_id: turnAgentId };
      if (forwardedConnectors) forwardedProps.connectors = forwardedConnectors;
      if (attachmentIds.length > 0) forwardedProps.attachment_artifact_ids = attachmentIds;
      return { forwardedProps };
    },
    [forwardedConnectors]
  );

  // Memoized so the queued handoff re-pose below can depend on it.
  const startTurn = useCallback(
    async (text: string, attachmentIds: string[] = []) => {
      if (!agentId) {
        throw new Error("No agent is selected for this turn");
      }
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.MESSAGE_SENT,
        { conversationId, projectId, agentId, agentName },
        {
          message_length: text.length,
          is_first_message: turns.length === 0,
          attachments_count: attachmentIds.length,
        }
      );
      supersedeCancel();
      // The user is driving again; end any deep-link hold so the new turn follows.
      releaseHold();
      agent.addMessage({
        id: crypto.randomUUID(),
        role: "user",
        content: text,
        // Extra field consumed by useTurns to render attachment cards on the
        // live user bubble. AG-UI doesn't re-validate added messages, so it
        // survives in memory; /connect re-supplies it from history on reload.
        ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
        // Attribute the turn to its handler now, at submit, rather than waiting
        // for RunStarted to stamp it: otherwise a turn sent right after a switch
        // has no handler yet and falls back to the conversation's current agent —
        // the agent just switched away from — for the whole pre-RunStarted window.
        // History re-serves the authoritative `turns.agent` on reload.
        agentId,
      } as Message);
      await agent.runAgent(runOptions(agentId, attachmentIds));
    },
    [
      agent,
      agentId,
      agentName,
      conversationId,
      projectId,
      turns.length,
      supersedeCancel,
      releaseHold,
      runOptions,
    ]
  );

  // The composer hands over the files it was holding; uploading them here (not on
  // attach) means an abandoned tray never leaves an orphaned artifact behind. The
  // run itself isn't awaited: it streams for the whole generation, and the
  // composer clears as soon as the attachments are safely stored.
  const handleComposerSubmit = async (text: string, files: File[]) => {
    if (!agentId) {
      throw new Error("No agent is selected for this turn");
    }
    const attachmentIds =
      files.length > 0 ? await uploadFilesToConversation(conversationId, files) : [];
    void startTurn(text, attachmentIds);
  };

  // Accepting a handoff suggestion switches to the suggested specialist and
  // re-poses the message that produced it, so the turn proceeds under the new
  // agent without the user retyping. The suggestion only ever attaches to the
  // latest turn, so the last user message is the one it answered. The re-pose is
  // queued rather than sent here: the switch has not flushed within this handler,
  // and the parent re-keys connectors on the new agent only once it does. The
  // effect below sends once both have settled — the hold the queued first message
  // takes — so the turn forwards the new agent's own connector selection. With no
  // user text to re-pose the switch alone still stands.
  const [pendingRepose, setPendingRepose] = useState<PendingRepose | null>(null);
  const handleAcceptSuggestion = (targetAgentId: string) => {
    if (agent.isRunning) return;
    onAgentSwitched(targetAgentId);
    let lastUserText: string | undefined;
    let lastUserAttachmentIds: string[] | undefined;
    for (let i = turns.length - 1; i >= 0; i--) {
      const turn = turns[i];
      if (turn.kind === "user") {
        lastUserText = turn.text;
        lastUserAttachmentIds = turn.attachmentIds;
        break;
      }
    }
    if (!lastUserText) return;
    setPendingRepose({
      agentId: targetAgentId,
      text: lastUserText,
      attachmentIds: lastUserAttachmentIds ?? [],
    });
  };
  useEffect(() => {
    if (!pendingRepose || connectorsLoading) return;
    setPendingRepose(null);
    // Superseded while it waited: the user sent another message, or the
    // next-turn agent moved off the accepted one.
    if (agent.isRunning || agentId !== pendingRepose.agentId) return;
    void startTurn(pendingRepose.text, pendingRepose.attachmentIds);
  }, [pendingRepose, connectorsLoading, agent, agentId, startTurn]);

  // Deliver this conversation's queued New Session first turn as its opening
  // message once connectors and the agent settle. See useFirstMessageDelivery for the
  // consume-at-submit contract that keeps it from dropping on early unmount.
  useFirstMessageDelivery(conversationId, startTurn, {
    resumeInFlight,
    connectorsLoading: Boolean(connectorsLoading),
    agentPending: !agentId,
    hasTurns: turns.length > 0,
  });

  // Answer a paused call and re-run. Every answer is staged as a tool message on
  // its call, which is what flips the card to a receipt; at send, an external
  // call's message goes on the wire as is and an approval's becomes a `resume`
  // entry.
  //
  // One response can pause several cards at once, so hold until every paused
  // call in the batch has an answer — the server won't take a partial one. On
  // failure, roll the whole batch back so every card returns to interactive.
  const stagedMessageIdsRef = useRef<string[]>([]);
  const handleAnswer = useCallback(
    async (toolCallId: string, answer: string) => {
      supersedeCancel();
      releaseHold();
      const messageId = crypto.randomUUID();
      agent.addMessage({ id: messageId, role: "tool", toolCallId, content: answer });
      stagedMessageIdsRef.current.push(messageId);

      // `addMessage` mutates `agent.messages` synchronously, so this sees the
      // answer just staged. Other cards in this pause still awaiting → hold.
      if (awaitingChannels(pending, agent.messages).size > 0) return;

      const batchIds = new Set(stagedMessageIdsRef.current);
      stagedMessageIdsRef.current = [];
      const rollBackBatch = () =>
        agent.setMessages(agent.messages.filter((m) => !batchIds.has(m.id)));
      // The resume continues the paused turn, so it names that turn's agent —
      // not the composer's next-turn pick, which may have moved since.
      const turnAgentId = latestTurnAgentId(agent.messages);
      if (!turnAgentId) {
        rollBackBatch();
        showError("This session has no agent to continue the paused turn.");
        return;
      }
      const resume = resumeEntries(pending, agent.messages);
      try {
        await agent.runAgent({
          ...(resume.length > 0 ? { resume } : {}),
          forwardedProps: { agent_id: turnAgentId },
        });
      } catch (err) {
        rollBackBatch();
        console.error("Failed to resume after answer", err);
        showError(answerErrorMessage(err));
      }
    },
    [agent, pending, showError, releaseHold, supersedeCancel]
  );

  const handleCancel = useCallback(async () => {
    if (isCancelling) {
      return;
    }
    trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.MESSAGE_CANCELLED, {
      conversationId,
      projectId,
      agentId,
      agentName,
    });
    await cancel();
  }, [conversationId, projectId, agentId, agentName, isCancelling, cancel]);

  const isEmpty = turns.length === 0;
  const [starterPromptValue, setStarterPromptValue] = useState<string | undefined>(undefined);
  // A quick action opens a brand-new session with its seed prompt queued; drop
  // it into the composer as an editable pre-fill (the user still hits send).
  usePendingComposerDraft(conversationId, setStarterPromptValue);

  // A send with no agent to run it fails server-side, so both composers below
  // stay disabled until one resolves. The explanation only shows once the
  // parent knows for sure — `projectNeedsAgent` gates it so it doesn't flash on
  // while the roster and session list are still loading.
  const noAgentNotice = projectNeedsAgent ? <NoAgentNotice /> : null;

  const composerProps = {
    isRunning: agent.isRunning,
    isCancelling,
    onCancel: handleCancel,
    onSubmit: handleComposerSubmit,
    skills,
    datasetNames,
    connectors,
    enabledConnectorNames,
    onToggleConnector,
    mcpGuidelines,
    contextBuffer,
    conversationId,
    contextDatasetNames,
    projectId,
    isOwner,
    displayName,
    agentId,
    featuredSessions,
    currentAgentId: agentId,
    onAgentSwitched,
  };

  return (
    <ToolUIProvider value={TOOL_UI_REGISTRY}>
      <McpAppEntriesProvider value={mcpAppEntries}>
        <SubagentEventsProvider value={subagentEvents}>
          <div className="flex flex-1 flex-col overflow-hidden">
            {isFeatured && (
              <div className="mx-auto w-full max-w-[60rem] px-6 pt-4">
                <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-xs text-primary">
                  This session is featured in this project — everyone in the sidebar can view it,
                  and your edits here are reflected there too.
                </div>
              </div>
            )}
            {isEmpty ? (
              <EmptyConversationView
                agent={emptyViewAgent}
                starterPrompts={starterPrompts}
                onPromptClick={setStarterPromptValue}
              >
                <Composer
                  {...composerProps}
                  disabled={!agentId}
                  starterPromptValue={starterPromptValue}
                />
                {noAgentNotice}
              </EmptyConversationView>
            ) : (
              <>
                <div
                  ref={scrollRef}
                  onScroll={recomputeJump}
                  className="flex-1 overflow-y-auto pt-4"
                >
                  <ol className="mx-auto flex max-w-[60rem] flex-col gap-4 px-6 pb-4" role="log">
                    <ConversationTurns
                      turns={turns}
                      agentsById={agentsById}
                      usableAgentIds={usableAgentIds}
                      onAcceptSuggestion={handleAcceptSuggestion}
                      onDismissSuggestion={onDismissSuggestion}
                      dismissedSuggestionTurnId={dismissedSuggestionTurnId}
                      projectId={projectId}
                      conversationId={conversationId}
                      isCancelling={isCancelling}
                      onAnswer={handleAnswer}
                      awaitingToolCalls={awaitingToolCalls}
                      highlightedTurnId={highlightedTurnId}
                    />
                  </ol>
                  <div
                    aria-hidden
                    className="pointer-events-none sticky bottom-0 h-8 bg-gradient-to-t from-background to-transparent"
                  />
                </div>

                <div className="relative px-4 pb-4">
                  {showJump && (
                    <button
                      type="button"
                      onClick={jumpToBottom}
                      aria-label={agent.isRunning ? "Jump to current response" : "Jump to bottom"}
                      className="absolute bottom-full left-1/2 z-20 mb-3 flex size-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-foreground"
                    >
                      {agent.isRunning ? (
                        <TypingDots />
                      ) : (
                        <ChevronDown className="size-4" aria-hidden />
                      )}
                    </button>
                  )}
                  <Composer
                    {...composerProps}
                    disabled={awaitingAnswer || reconciling || !agentId}
                    placeholder={
                      awaitingAnswer ? "Answer the question above to continue" : undefined
                    }
                  />
                  {noAgentNotice}
                </div>
              </>
            )}
          </div>
        </SubagentEventsProvider>
      </McpAppEntriesProvider>
    </ToolUIProvider>
  );
};

// Says why the composer is disabled. Visible text rather than the input's
// placeholder: a pre-filled draft (a quick action's seed prompt) hides a
// placeholder, and a disabled input can't be cleared to reveal it.
const NoAgentNotice = () => (
  <p role="status" className="mt-2 text-center text-sm text-muted-foreground">
    No agent in this project is available to you, so you cannot send a message.
  </p>
);

const TypingDots = () => (
  <span className="flex items-end gap-0.5" aria-hidden>
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "0ms" }}
    />
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "150ms" }}
    />
    <span
      className="block size-1 animate-bounce rounded-full bg-current"
      style={{ animationDelay: "300ms" }}
    />
  </span>
);
